// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Jotai from "jotai";
import * as React from "react";
import * as Caml_obj from "rescript/lib/es6/caml_obj.js";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as Core__JSON from "@rescript/core/src/Core__JSON.bs.js";
import * as Core__Array from "@rescript/core/src/Core__Array.bs.js";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Utils$OrcaPaymentPage from "./Utils.bs.js";
import * as JotaiAtoms$OrcaPaymentPage from "./JotaiAtoms.bs.js";
import * as Validation$OrcaPaymentPage from "../../shared-code/sdk-utils/validation/Validation.bs.js";
import * as PaymentUtils$OrcaPaymentPage from "./PaymentUtils.bs.js";
import * as SdkConfigParser$OrcaPaymentPage from "../../shared-code/sdk-utils/utils/SdkConfigParser.bs.js";
import * as LocaleStringTypes$OrcaPaymentPage from "../LocaleStrings/LocaleStringTypes.bs.js";
import * as SuperpositionHelper$OrcaPaymentPage from "../../shared-code/sdk-utils/utils/SuperpositionHelper.bs.js";
import * as ConfigurationService$OrcaPaymentPage from "../../shared-code/sdk-utils/hooks/ConfigurationService.bs.js";
import * as PaymentMethodsRecord$OrcaPaymentPage from "../Payments/PaymentMethodsRecord.bs.js";

var billingPrefix = "payment_method_data.billing.";

function filterByActiveFields(flatValues, activeFields) {
  return Object.fromEntries(Core__Array.filterMap(activeFields, (function (field) {
                    var path = field.confirmRequestWritePath;
                    return Core__Option.map(flatValues[path], (function (value) {
                                  return [
                                          path,
                                          value
                                        ];
                                }));
                  })));
}

function getFieldTestId(path) {
  var parts = path.split(".");
  return Core__Option.getOr(parts[parts.length - 1 | 0], path);
}

function lookupLocaleKey(key, locale) {
  return Core__Option.flatMap(Core__Option.flatMap(Core__JSON.Decode.object(locale), (function (dict) {
                    return dict[key];
                  })), Core__JSON.Decode.string);
}

function resolveFieldTexts(field, localeObject) {
  var name = field.merchantProvidedDisplayName;
  var label = name !== undefined ? name : Core__Option.getOr(Core__Option.flatMap(field.labelLocalizationKey, (function (key) {
                return lookupLocaleKey(key, localeObject);
              })), field.defaultLabelText);
  var text = field.merchantProvidedPlaceholderText;
  var placeholder;
  if (text !== undefined) {
    placeholder = text;
  } else {
    var defaultPlaceholder = field.defaultPlaceholderText.length > 0 ? field.defaultPlaceholderText : label;
    placeholder = Core__Option.getOr(Core__Option.flatMap(field.placeholderLocalizationKey, (function (key) {
                return lookupLocaleKey(key, localeObject);
              })), defaultPlaceholder);
  }
  return {
          label: label,
          placeholder: placeholder
        };
}

function emptyMessageForField(field, localeObject) {
  var match = field.labelLocalizationKey;
  if (match === undefined) {
    return ;
  }
  switch (match) {
    case "cityLabel" :
        return localeObject.cityEmptyText;
    case "emailLabel" :
        return localeObject.emailEmptyText;
    case "line1Label" :
        return localeObject.line1EmptyText;
    case "line2Label" :
        return localeObject.line2EmptyText;
    case "postalCodeLabel" :
        return localeObject.postalCodeEmptyText;
    case "sourceBankAccountIdLabel" :
        return localeObject.sourceBankAccountIdEmptyText;
    case "stateLabel" :
        return localeObject.stateEmptyText;
    case "vpaIdLabel" :
        return localeObject.vpaIdEmptyText;
    default:
      return ;
  }
}

function resolveValidator(field, localeObject) {
  var requiredRule = field.isRequired ? [{
        TAG: "Required",
        _0: emptyMessageForField(field, localeObject)
      }] : [];
  var match = field.validationRuleType;
  var semanticRule;
  if (match !== undefined) {
    switch (match) {
      case "bank_account_number" :
          semanticRule = ["BankAccountNumber"];
          break;
      case "blik_code" :
          semanticRule = ["BlikCode"];
          break;
      case "date_of_birth" :
          semanticRule = ["DateOfBirth"];
          break;
      case "email" :
          semanticRule = ["Email"];
          break;
      case "first_name" :
          semanticRule = ["FirstName"];
          break;
      case "gift_card_number" :
          semanticRule = ["GiftCardNumber"];
          break;
      case "gift_card_pin" :
          semanticRule = ["GiftCardPin"];
          break;
      case "iban" :
          semanticRule = ["IBAN"];
          break;
      case "last_name" :
          semanticRule = ["LastName"];
          break;
      case "phone" :
          semanticRule = ["Phone"];
          break;
      case "pix_cnpj" :
          semanticRule = ["PixCNPJ"];
          break;
      case "pix_cpf" :
          semanticRule = ["PixCPF"];
          break;
      case "pix_key" :
          semanticRule = ["PixKey"];
          break;
      case "regex" :
          var pattern = field.validationRegexPattern;
          semanticRule = pattern !== undefined ? requiredRule.concat([{
                    TAG: "Generic",
                    _0: pattern
                  }]) : requiredRule;
          break;
      case "routing_number" :
          semanticRule = ["RoutingNumber"];
          break;
      default:
        semanticRule = [];
    }
  } else {
    semanticRule = requiredRule;
  }
  var maxLengthRule = [{
      TAG: "MaxLength",
      _0: Core__Option.getOr(field.maxInputLength, 255)
    }];
  var rules = Belt_Array.concatMany([
        semanticRule,
        requiredRule,
        maxLengthRule
      ]);
  return Validation$OrcaPaymentPage.createFieldValidator(rules, [], LocaleStringTypes$OrcaPaymentPage.toValidationLocale(localeObject));
}

function findCryptoCurrencyField(allFields) {
  return allFields.find(function (field) {
              return field.fieldRenderType === "CryptoCurrency";
            });
}

function isCombinedPhoneRow(items) {
  if (items.some(function (field) {
          return field.fieldRenderType === "Phone";
        })) {
    return items.some(function (field) {
                return field.fieldRenderType === "PhoneCountryCode";
              });
  } else {
    return false;
  }
}

function getCombinedPhoneRowLabel(items, localeObject) {
  return Core__Option.getOr(Core__Option.map(items.find(function (field) {
                      return field.fieldRenderType === "Phone";
                    }), (function (phoneField) {
                    return resolveFieldTexts(phoneField, localeObject).label;
                  })), "");
}

function getComputedLanguagePreferenceValue(locale, options) {
  if (options.includes(locale.toUpperCase().split("-").join("_"))) {
    return locale;
  } else {
    return "en";
  }
}

function extractValuesFromPMLRequiredFields(requiredFields) {
  return Core__Array.reduce(requiredFields, {}, (function (acc, field) {
                if (field.required_field !== "") {
                  acc[field.required_field] = field.value;
                }
                return acc;
              }));
}

function buildSuperpositionBaseContext(paymentMethod, paymentMethodType, platform, country, paymentMethodListValue, accountConfig, contextUsed) {
  var match = paymentMethodListValue.payment_type;
  var mandateType;
  switch (match) {
    case "NEW_MANDATE" :
    case "SETUP_MANDATE" :
        mandateType = "mandate";
        break;
    case "NORMAL" :
    case "NONE" :
        mandateType = "non_mandate";
        break;
    
  }
  var profile = Core__Option.flatMap(accountConfig, (function (ac) {
          return ac.profile;
        }));
  var collectBilling = SdkConfigParser$OrcaPaymentPage.getCollectBillingDetailsFromWalletConnector(profile);
  var collectShipping = SdkConfigParser$OrcaPaymentPage.getCollectShippingDetailsFromWalletConnector(profile);
  var match$1 = SdkConfigParser$OrcaPaymentPage.getProfileContext(contextUsed);
  return {
          payment_method: paymentMethod,
          payment_method_type: paymentMethodType,
          platform: platform,
          country: country,
          mandate_type: mandateType,
          always_collect_shipping_details_from_wallet_connector: collectShipping,
          always_collect_billing_details_from_wallet_connector: collectBilling,
          profile_id: match$1[0],
          processor_merchant_id: match$1[1],
          organization_id: match$1[2]
        };
}

function checkIfNameIsValid(requiredFieldsType, paymentMethodFields, field) {
  return Core__Array.reduce(requiredFieldsType.filter(function (required_field) {
                  return required_field.field_type === paymentMethodFields;
                }), true, (function (acc, item) {
                var fieldNameArr = field.value.split(" ");
                var requiredFieldsArr = item.required_field.split(".");
                var match = Core__Option.getOr(requiredFieldsArr[requiredFieldsArr.length - 1 | 0], "");
                var fieldValue;
                switch (match) {
                  case "first_name" :
                      fieldValue = Core__Option.getOr(fieldNameArr[0], "");
                      break;
                  case "last_name" :
                      fieldValue = Core__Option.getOr(fieldNameArr[1], "");
                      break;
                  default:
                    fieldValue = field.value;
                }
                if (acc) {
                  return fieldValue !== "";
                } else {
                  return false;
                }
              }));
}

function usePaymentMethodTypeFromList(paymentMethodListValue, paymentMethod, paymentMethodType) {
  return React.useMemo((function () {
                return Core__Option.getOr(PaymentMethodsRecord$OrcaPaymentPage.getPaymentMethodTypeFromList(paymentMethodListValue, paymentMethod, PaymentUtils$OrcaPaymentPage.getPaymentMethodName(paymentMethod, paymentMethodType)), PaymentMethodsRecord$OrcaPaymentPage.defaultPaymentMethodType);
              }), [
              paymentMethodListValue,
              paymentMethod,
              paymentMethodType
            ]);
}

function useSuperpositionRequiredFields(paymentMethod, paymentMethodType) {
  var sdkConfigsValue = Jotai.useAtomValue(PaymentUtils$OrcaPaymentPage.sdkConfigsValue);
  var paymentMethodListValue = Jotai.useAtomValue(PaymentUtils$OrcaPaymentPage.paymentMethodListValue);
  var userCountryName = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.userCountry);
  var country = Utils$OrcaPaymentPage.getCountryCode(userCountryName).isoAlpha2;
  var match = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  var billingAddress = match.billingAddress;
  var isUseBillingAddress = billingAddress.isUseBillingAddress;
  var match$1 = billingAddress.usePrefilledValues;
  var suppressBillingPrefill;
  suppressBillingPrefill = match$1 === "Auto" ? false : isUseBillingAddress;
  var rawConfigs = sdkConfigsValue.raw_configs;
  var getSuperpositionFinalFields = ConfigurationService$OrcaPaymentPage.useConfigurationService(rawConfigs);
  var configPaymentMethodType = PaymentUtils$OrcaPaymentPage.getPaymentMethodName(paymentMethod, paymentMethodType);
  var eligibleConnectors = React.useMemo((function () {
          return SdkConfigParser$OrcaPaymentPage.getEligibleConnectorsFromPaymentMethods(sdkConfigsValue.payment_methods, paymentMethod, configPaymentMethodType).map(function (prim) {
                      return prim;
                    });
        }), [
        sdkConfigsValue.payment_methods,
        paymentMethod,
        configPaymentMethodType
      ]);
  var intentData = paymentMethodListValue.intent_data.intentDataObject;
  var superpositionBaseContext = React.useMemo((function () {
          return buildSuperpositionBaseContext(paymentMethod, configPaymentMethodType, "web", country, paymentMethodListValue, sdkConfigsValue.account_config, sdkConfigsValue.context_used);
        }), [
        paymentMethod,
        configPaymentMethodType,
        country,
        paymentMethodListValue,
        sdkConfigsValue.account_config,
        sdkConfigsValue.context_used
      ]);
  var match$2 = React.useMemo((function () {
          return getSuperpositionFinalFields(eligibleConnectors, superpositionBaseContext, intentData, isUseBillingAddress, suppressBillingPrefill);
        }), [
        getSuperpositionFinalFields,
        eligibleConnectors,
        superpositionBaseContext,
        intentData,
        isUseBillingAddress,
        suppressBillingPrefill
      ]);
  var resolutionContext = React.useMemo((function () {
          return {
                  rawConfigs: rawConfigs,
                  configPaymentMethodType: configPaymentMethodType,
                  eligibleConnectors: eligibleConnectors,
                  superpositionBaseContext: superpositionBaseContext
                };
        }), [
        rawConfigs,
        configPaymentMethodType,
        eligibleConnectors,
        superpositionBaseContext
      ]);
  return [
          match$2[0],
          match$2[1],
          match$2[2],
          resolutionContext
        ];
}

function useAreWalletRequiredFieldsPrefilled(paymentMethodType) {
  var match = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  var match$1 = useSuperpositionRequiredFields("wallet", paymentMethodType);
  var missingRequiredFields = match$1[1];
  var relevant = match.billingAddress.isUseBillingAddress ? missingRequiredFields.filter(function (field) {
          return !field.confirmRequestWritePath.startsWith(billingPrefix);
        }) : missingRequiredFields;
  return relevant.length === 0;
}

function splitName(str) {
  if (str === undefined) {
    return [
            "",
            ""
          ];
  }
  if (str === "") {
    return [
            "",
            ""
          ];
  }
  var lastSpaceIndex = str.lastIndexOf(" ");
  if (lastSpaceIndex === -1) {
    return [
            str,
            ""
          ];
  }
  var first = str.slice(0, lastSpaceIndex);
  var last = str.slice(lastSpaceIndex + 1 | 0, str.length);
  return [
          first,
          last
        ];
}

var defaultWalletRequiredFieldPaths = [
  "email",
  "payment_method_data.billing.address.first_name",
  "payment_method_data.billing.address.last_name",
  "payment_method_data.billing.address.city",
  "payment_method_data.billing.address.state",
  "payment_method_data.billing.address.country",
  "payment_method_data.billing.address.line1",
  "payment_method_data.billing.address.zip"
];

function getApplePayRequiredFields(billingContact, shippingContact, requiredFieldPathsOpt) {
  var requiredFieldPaths = requiredFieldPathsOpt !== undefined ? requiredFieldPathsOpt : defaultWalletRequiredFieldPaths;
  var billingCountryCode = billingContact.countryCode.toUpperCase();
  var shippingCountryCode = shippingContact.countryCode.toUpperCase();
  return Core__Array.reduce(requiredFieldPaths, {}, (function (acc, path) {
                var fieldVal;
                switch (path) {
                  case "payment_method_data.billing.address.city" :
                      fieldVal = billingContact.locality;
                      break;
                  case "payment_method_data.billing.address.country" :
                      fieldVal = billingCountryCode;
                      break;
                  case "payment_method_data.billing.address.first_name" :
                      fieldVal = billingContact.givenName;
                      break;
                  case "payment_method_data.billing.address.last_name" :
                      fieldVal = billingContact.familyName;
                      break;
                  case "payment_method_data.billing.address.line1" :
                      fieldVal = Core__Option.getOr(billingContact.addressLines[0], "");
                      break;
                  case "payment_method_data.billing.address.line2" :
                      fieldVal = Core__Option.getOr(billingContact.addressLines[1], "");
                      break;
                  case "payment_method_data.billing.address.state" :
                      fieldVal = billingContact.administrativeArea;
                      break;
                  case "payment_method_data.billing.address.zip" :
                      fieldVal = billingContact.postalCode;
                      break;
                  case "payment_method_data.billing.phone.number" :
                      fieldVal = shippingContact.phoneNumber;
                      break;
                  case "email" :
                  case "payment_method_data.billing.email" :
                  case "payment_method_data.email" :
                      fieldVal = shippingContact.emailAddress;
                      break;
                  case "shipping.address.city" :
                      fieldVal = shippingContact.locality;
                      break;
                  case "shipping.address.country" :
                      fieldVal = shippingCountryCode;
                      break;
                  case "shipping.address.first_name" :
                      fieldVal = shippingContact.givenName;
                      break;
                  case "shipping.address.last_name" :
                      fieldVal = shippingContact.familyName;
                      break;
                  case "shipping.address.line1" :
                      fieldVal = Core__Option.getOr(shippingContact.addressLines[0], "");
                      break;
                  case "shipping.address.line2" :
                      fieldVal = Core__Option.getOr(shippingContact.addressLines[1], "");
                      break;
                  case "shipping.address.state" :
                      fieldVal = shippingContact.administrativeArea;
                      break;
                  case "shipping.address.zip" :
                      fieldVal = shippingContact.postalCode;
                      break;
                  default:
                    fieldVal = "";
                }
                if (fieldVal !== "") {
                  acc[path] = fieldVal;
                }
                return acc;
              }));
}

function getGooglePayRequiredFields(billingContact, shippingContact, requiredFieldPathsOpt, email) {
  var requiredFieldPaths = requiredFieldPathsOpt !== undefined ? requiredFieldPathsOpt : defaultWalletRequiredFieldPaths;
  var match = splitName(billingContact.name);
  var billingLastName = match[1];
  var billingFirstName = match[0];
  var match$1 = splitName(shippingContact.name);
  var shippingLastName = match$1[1];
  var shippingFirstName = match$1[0];
  return Core__Array.reduce(requiredFieldPaths, {}, (function (acc, path) {
                var fieldVal;
                switch (path) {
                  case "payment_method_data.billing.address.city" :
                      fieldVal = billingContact.locality;
                      break;
                  case "payment_method_data.billing.address.country" :
                      fieldVal = billingContact.countryCode;
                      break;
                  case "payment_method_data.billing.address.first_name" :
                      fieldVal = billingFirstName;
                      break;
                  case "payment_method_data.billing.address.last_name" :
                      fieldVal = billingLastName;
                      break;
                  case "payment_method_data.billing.address.line1" :
                      fieldVal = billingContact.address1;
                      break;
                  case "payment_method_data.billing.address.line2" :
                      fieldVal = billingContact.address2;
                      break;
                  case "payment_method_data.billing.address.state" :
                      fieldVal = billingContact.administrativeArea;
                      break;
                  case "payment_method_data.billing.address.zip" :
                      fieldVal = billingContact.postalCode;
                      break;
                  case "payment_method_data.billing.phone.number" :
                      fieldVal = shippingContact.phoneNumber.replaceAll(" ", "").replaceAll("-", "");
                      break;
                  case "email" :
                  case "payment_method_data.billing.email" :
                  case "payment_method_data.email" :
                      fieldVal = email;
                      break;
                  case "shipping.address.city" :
                      fieldVal = shippingContact.locality;
                      break;
                  case "shipping.address.country" :
                      fieldVal = shippingContact.countryCode;
                      break;
                  case "shipping.address.first_name" :
                      fieldVal = shippingFirstName;
                      break;
                  case "shipping.address.last_name" :
                      fieldVal = shippingLastName;
                      break;
                  case "shipping.address.line1" :
                      fieldVal = shippingContact.address1;
                      break;
                  case "shipping.address.line2" :
                      fieldVal = shippingContact.address2;
                      break;
                  case "shipping.address.state" :
                      fieldVal = shippingContact.administrativeArea;
                      break;
                  case "shipping.address.zip" :
                      fieldVal = shippingContact.postalCode;
                      break;
                  default:
                    fieldVal = "";
                }
                if (fieldVal !== "") {
                  acc[path] = fieldVal;
                }
                return acc;
              }));
}

function getPaypalRequiredFields(details, requiredFields) {
  var match = splitName(details.shippingAddress.recipientName);
  var shippingLastName = match[1];
  var shippingFirstName = match[0];
  return Core__Array.reduce(requiredFields, {}, (function (acc, fieldConfig) {
                var match = fieldConfig.confirmRequestWritePath;
                var fieldVal;
                switch (match) {
                  case "shipping.address.city" :
                      fieldVal = details.shippingAddress.city;
                      break;
                  case "shipping.address.country" :
                      fieldVal = details.shippingAddress.countryCode;
                      break;
                  case "shipping.address.first_name" :
                      fieldVal = shippingFirstName;
                      break;
                  case "shipping.address.last_name" :
                      fieldVal = shippingLastName;
                      break;
                  case "shipping.address.line1" :
                      fieldVal = details.shippingAddress.line1;
                      break;
                  case "shipping.address.line2" :
                      fieldVal = details.shippingAddress.line2;
                      break;
                  case "shipping.address.state" :
                      fieldVal = Core__Option.getOr(details.shippingAddress.state, "");
                      break;
                  case "shipping.address.zip" :
                      fieldVal = details.shippingAddress.postalCode;
                      break;
                  case "payment_method_data.billing.email" :
                  case "payment_method_data.email" :
                  case "shipping.email" :
                      fieldVal = details.email;
                      break;
                  case "payment_method_data.billing.phone.number" :
                  case "shipping.phone.number" :
                      fieldVal = details.phone;
                      break;
                  default:
                    fieldVal = undefined;
                }
                Core__Option.mapOr(fieldVal, undefined, (function (fieldVal) {
                        acc[fieldConfig.confirmRequestWritePath] = fieldVal;
                      }));
                return acc;
              }));
}

function getKlarnaRequiredFields(shippingContact, requiredFields) {
  return Core__Array.reduce(requiredFields, {}, (function (acc, fieldConfig) {
                var match = fieldConfig.confirmRequestWritePath;
                var fieldVal;
                switch (match) {
                  case "shipping.address.city" :
                      fieldVal = shippingContact.city;
                      break;
                  case "shipping.address.country" :
                      fieldVal = shippingContact.country;
                      break;
                  case "shipping.address.first_name" :
                      fieldVal = shippingContact.given_name;
                      break;
                  case "shipping.address.last_name" :
                      fieldVal = shippingContact.family_name;
                      break;
                  case "shipping.address.line1" :
                      fieldVal = shippingContact.street_address;
                      break;
                  case "shipping.address.state" :
                      fieldVal = shippingContact.region;
                      break;
                  case "shipping.address.zip" :
                      fieldVal = shippingContact.postal_code;
                      break;
                  case "payment_method_data.billing.email" :
                  case "payment_method_data.email" :
                  case "shipping.email" :
                      fieldVal = shippingContact.email;
                      break;
                  case "payment_method_data.billing.phone.number" :
                  case "shipping.phone.number" :
                      fieldVal = shippingContact.phone;
                      break;
                  default:
                    fieldVal = "";
                }
                if (fieldVal !== "") {
                  acc[fieldConfig.confirmRequestWritePath] = fieldVal;
                }
                return acc;
              }));
}

function applyBillingDetailsOverride(initialValues, billingDetails) {
  var set = function (path, value) {
    if (value !== "") {
      SuperpositionHelper$OrcaPaymentPage.setValueAtNestedPath(initialValues, path.split("."), value);
      return ;
    }
    
  };
  var name = billingDetails.name;
  if (name !== "") {
    var match = Utils$OrcaPaymentPage.splitFullName(name);
    set("payment_method_data.billing.address.first_name", match[0]);
    set("payment_method_data.billing.address.last_name", match[1]);
  }
  set("payment_method_data.billing.email", billingDetails.email);
  set("payment_method_data.billing.phone.number", billingDetails.phone);
  set("payment_method_data.billing.address.line1", billingDetails.address.line1);
  set("payment_method_data.billing.address.line2", billingDetails.address.line2);
  set("payment_method_data.billing.address.city", billingDetails.address.city);
  set("payment_method_data.billing.address.state", billingDetails.address.state);
  set("payment_method_data.billing.address.country", billingDetails.address.country);
  set("payment_method_data.billing.address.zip", billingDetails.address.postal_code);
  return initialValues;
}

function useLogDynamicFieldsRendered(fields, paymentMethod, resolutionContext, isSavedCardFlowOpt) {
  var isSavedCardFlow = isSavedCardFlowOpt !== undefined ? isSavedCardFlowOpt : false;
  var loggerState = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.loggerAtom);
  var lastLoggedKey = React.useRef("");
  var superpositionBaseContext = resolutionContext.superpositionBaseContext;
  var eligibleConnectors = resolutionContext.eligibleConnectors;
  var configPaymentMethodType = resolutionContext.configPaymentMethodType;
  var rawConfigs = resolutionContext.rawConfigs;
  React.useEffect((function () {
          if (!isSavedCardFlow && Core__Option.isSome(rawConfigs)) {
            var dedupeKey = paymentMethod + "|" + configPaymentMethodType;
            if (lastLoggedKey.current !== dedupeKey) {
              lastLoggedKey.current = dedupeKey;
              var fieldsJson = fields.map(function (field) {
                    return Object.fromEntries([
                                [
                                  "field_type",
                                  field.fieldRenderType
                                ],
                                [
                                  "write_path",
                                  field.confirmRequestWritePath
                                ],
                                [
                                  "intent_data_read_path",
                                  Core__Option.getOr(Core__Option.map(field.intentDataReadPath, (function (prim) {
                                              return prim;
                                            })), null)
                                ],
                                [
                                  "is_required",
                                  field.isRequired
                                ],
                                [
                                  "validation_rule_type",
                                  Core__Option.getOr(Core__Option.map(field.validationRuleType, (function (prim) {
                                              return prim;
                                            })), null)
                                ],
                                [
                                  "validation_regex_pattern",
                                  Core__Option.getOr(Core__Option.map(field.validationRegexPattern, (function (prim) {
                                              return prim;
                                            })), null)
                                ]
                              ]);
                  });
              var payload = JSON.stringify(Object.fromEntries([
                        [
                          "superposition_base_context",
                          superpositionBaseContext
                        ],
                        [
                          "eligible_connectors",
                          eligibleConnectors
                        ],
                        [
                          "field_count",
                          fields.length
                        ],
                        [
                          "fields",
                          fieldsJson
                        ]
                      ]));
              loggerState.setLogInfo(payload, "DYNAMIC_FIELDS_RENDERED", undefined, undefined, undefined, undefined, paymentMethod);
            }
            
          }
          
        }), [
        fields,
        paymentMethod,
        configPaymentMethodType,
        rawConfigs,
        isSavedCardFlow,
        eligibleConnectors,
        superpositionBaseContext,
        loggerState
      ]);
}

function useSyncEmitAddressAtoms() {
  var country = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.userCountry);
  var setUserAddressState = Jotai.useSetAtom(JotaiAtoms$OrcaPaymentPage.userAddressState);
  var setUserAddressPincode = Jotai.useSetAtom(JotaiAtoms$OrcaPaymentPage.userAddressPincode);
  return function (flatValues) {
    var readBillingValue = function (path) {
      return Core__Option.map(flatValues[path], (function (json) {
                    return Core__Option.getOr(Core__JSON.Decode.string(json), "");
                  }));
    };
    var countryFromForm = Core__Option.getOr(readBillingValue("payment_method_data.billing.address.country"), "");
    var countryIso = countryFromForm !== "" ? countryFromForm : Utils$OrcaPaymentPage.getCountryCode(country).isoAlpha2;
    var stateCode = readBillingValue("payment_method_data.billing.address.state");
    if (stateCode !== undefined) {
      var stateName = stateCode.trim() === "" ? "" : Utils$OrcaPaymentPage.getStateNameFromCode(stateCode, countryIso);
      setUserAddressState(function (prev) {
            if (prev.value === stateName) {
              return prev;
            }
            var newrecord = Caml_obj.obj_dup(prev);
            newrecord.value = stateName;
            return newrecord;
          });
    }
    var zip = readBillingValue("payment_method_data.billing.address.zip");
    if (zip !== undefined) {
      return setUserAddressPincode(function (prev) {
                  if (prev.value === zip) {
                    return prev;
                  }
                  var newrecord = Caml_obj.obj_dup(prev);
                  newrecord.value = zip;
                  return newrecord;
                });
    }
    
  };
}

export {
  billingPrefix ,
  filterByActiveFields ,
  getFieldTestId ,
  lookupLocaleKey ,
  resolveFieldTexts ,
  emptyMessageForField ,
  resolveValidator ,
  findCryptoCurrencyField ,
  isCombinedPhoneRow ,
  getCombinedPhoneRowLabel ,
  getComputedLanguagePreferenceValue ,
  extractValuesFromPMLRequiredFields ,
  buildSuperpositionBaseContext ,
  checkIfNameIsValid ,
  usePaymentMethodTypeFromList ,
  useSuperpositionRequiredFields ,
  useAreWalletRequiredFieldsPrefilled ,
  splitName ,
  defaultWalletRequiredFieldPaths ,
  getApplePayRequiredFields ,
  getGooglePayRequiredFields ,
  getPaypalRequiredFields ,
  getKlarnaRequiredFields ,
  applyBillingDetailsOverride ,
  useLogDynamicFieldsRendered ,
  useSyncEmitAddressAtoms ,
}
/* jotai Not a pure module */
